import React, { useCallback, useState } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import { groupBy } from 'lodash';
import classNames from 'classnames';
import useScreen from 'hooks/useScreen';
import Placeholder from 'components/consumer/Placeholder';
import BrowserOnly from 'components/consumer/BrowserOnly';
import PDPActiveFabric from 'components/consumer/PDP/PDPActiveFabric';
import PDPDecideLaterPromo from 'components/consumer/PDP/PDPDecideLaterPromo';
import PDPUpholsterySortBy from 'components/consumer/PDP/PDPUpholsterySortBy';
import MaterialThumbnailGroup from 'components/consumer/MaterialThumbnailGroup';
import PDPUpholsteryFilters from 'components/consumer/PDP/PDPUpholsteryFilters';
import PDPFabricDetailSidebar from 'components/consumer/PDP/PDPFabricDetailSidebar';
import PDPFreeSwatchKitButton from 'components/consumer/PDP/PDPFreeSwatchKitButton';
import PDPOrderSwatchesSidebar from 'components/consumer/PDP/PDPOrderSwatchesSidebar';
import usePostHogFeatureFlag from 'hooks/usePostHogFeatureFlag';
import PDPUpholsteryColorFilters from 'components/consumer/PDP/PDPUpholsteryColorFilters';
import {
  PDP_FILTER_TYPES,
  PDP_CUSTOMIZE_TAB_ID,
  PDP_READYTOSHIP_TAB_ID,
  PDP_DECIDELATER_TAB_ID,
  FABRIC_PRODUCT_OPTION_ID,
} from 'commons/constants';

import { materialPropType, productFilterTypes } from 'types/PDPTypes';
import useFabricDetails from 'data-hooks/useFabricDetails';
import useIsTouchDevice from 'hooks/useIsTouchDevice';
import usePdpIsModularBYOProduct from 'components/consumer/PDP/state/context/usePdpIsModularBYOProduct';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import FlyoutContainer from '../../FlyoutContainer';
import PDPSelectUpholsteryDecideLater from './PDPSelectUpholsteryDecideLater';

const PDPCompareFabricsTool = loadable(() =>
  import('../PDPCompareFabricsTool')
);
const PDPFullScreenView = loadable(() =>
  import('components/consumer/PDP/PDPFullScreenView')
);

const tiers = {
  1: {
    title: 'Tier 1',
    desc:
      'Readily available fabrics that bring an eye-catching look to your piece at a great value.',
  },
  2: {
    title: 'Tier 2',
    desc:
      'Kick things up a notch with fabrics that show off more rich colors, textures, and durability.',
  },
  3: {
    title: 'Tier 3',
    desc:
      'Got an active home? Check out these fabrics that showcase top notch performance finishes while still looking stylish.',
  },
  4: {
    title: 'Tier 4',
    desc:
      'As luxe as it gets, this tier includes our branded performance fabrics and stain resistant finish fabrics, ready to take on all life throws its way in sophisticated style.',
  },
};

const PDPSelectUpholsteryUI = ({
  allMaterialsLength,
  disabledMaterialIds,
  fabricFavProps,
  filterCounts = {},
  filterType,
  flashSaleOptionIds = [],
  isEditOrder = false,
  isSelectedOptionsQuickship,
  loading,
  materials,
  mode = 'pdp',
  onMoreMaterialsClick,
  onOptionValueHover,
  onOptionValueSelection,
  selectedColor,
  selectedMaterial,
  selectedOptionsStockQty,
  setFilterType,
  setSelectedColor,
  upholsteryFilteredEvent,
  upholsteryOptionId,
  upholsteryOptionName,
  isClearanceFabricAvailable,
}) => {
  const { isDesktop, isMobile } = useScreen();
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();
  const [selectedSortOption, setSelectedSortOption] = useState();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isFullScreenOpen, setFullScreenOpen] = useState(false);
  const [isCompareToolOpen, setCompareToolOpen] = useState(false);
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const pdpFabricFilters = usePostHogFeatureFlag('PDPFabricFilters');
  const isMultiSelectFilters = pdpFabricFilters === 'test' || isNewConsumerApp;
  const [sideBarSelectedFabricId, setsideBarSelectedFabricId] = useState(
    selectedMaterial?.materialId
  );

  const [isOrderSwatchesSidebarOpen, setIsOrderSwatchesSidebarOpen] = useState(
    false
  );

  const [galleryType, setGalleryType] = useState('');
  const [sortByFlyOutContent, setSortByFlyOutContent] = useState({});
  const [isSortByFlyOutVisible, setIsSortByFlyOutVisible] = useState(false);

  const {
    data: { selectedFabric },
  } = useFabricDetails(selectedMaterial?.materialId);

  const history = useHistory();
  const isTouchDevice = useIsTouchDevice();

  const isPdpMode = mode === 'pdp';

  const materialsGroupedByFamily =
    (filterType === PDP_FILTER_TYPES.ALL || isMultiSelectFilters) &&
    selectedSortOption === 'family'
      ? groupBy(materials, 'groupName')
      : {};
  const materialsGroupedByTier =
    (filterType === PDP_FILTER_TYPES.ALL || isMultiSelectFilters) &&
    selectedSortOption === 'tier'
      ? groupBy(materials, 'tier')
      : {};

  const ungroupedMaterialsArray = [...materials];
  let moreMaterialsCount;

  if (mode === 'quickview') {
    // If in quickview mode, limit the number of materials shown
    const quickviewNumberOfFabricsToShow = isMobile ? 5 : 8;

    if (allMaterialsLength > quickviewNumberOfFabricsToShow) {
      moreMaterialsCount = allMaterialsLength - quickviewNumberOfFabricsToShow;
      ungroupedMaterialsArray.length = quickviewNumberOfFabricsToShow;
    }
  }

  const getFabricFavInfoToShow = useCallback(
    upholsteryOptionNameVal => {
      const tapOrClick = isTouchDevice ? 'tap' : 'click';

      return upholsteryOptionNameVal === 'Fabric'
        ? `Double-${tapOrClick} a fabric to add to your favorites`
        : upholsteryOptionNameVal === 'Leather'
        ? `Double-${tapOrClick} a leather to add to your favorites`
        : `Double tap to favorite`;
    },
    [isTouchDevice]
  );

  const onFabricInfoClick = useCallback(
    newMaterialId => {
      setsideBarSelectedFabricId(newMaterialId);

      if (isFullScreenOpen || isCompareToolOpen) {
        setSidebarOpen(true);
      }
    },
    [isFullScreenOpen, isCompareToolOpen]
  );

  const closeCompareToolModal = () => {
    setCompareToolOpen(false);
    history.goBack();
  };

  return (
    <>
      {isFullScreenOpen ? (
        <BrowserOnly>
          <PDPFullScreenView
            isOpen={isFullScreenOpen}
            onClose={() => {
              setFullScreenOpen(false);
            }}
            isEditOrder={isEditOrder}
            materials={materials}
            loading={loading}
            filterType={filterType}
            selectedColor={selectedColor}
            setFilterType={filter => {
              setFilterType(filter);
              upholsteryFilteredEvent(
                filter,
                selectedColor,
                selectedSortOption
              );
            }}
            setSelectedColor={color => {
              setSelectedColor(color);
              upholsteryFilteredEvent(filterType, color, selectedSortOption);
            }}
            selectedMaterial={selectedMaterial}
            onFabricInfoClick={newMaterialId => {
              onFabricInfoClick(newMaterialId);
            }}
            filterCounts={filterCounts}
            fabricFavProps={fabricFavProps}
            fabricFavInfoToShow={getFabricFavInfoToShow(upholsteryOptionName)}
          />
        </BrowserOnly>
      ) : null}

      <Switch>
        <Route path="/:category/:slug/compare">
          <BrowserOnly>
            <PDPCompareFabricsTool
              isOpen
              onClose={closeCompareToolModal}
              materialsProp={materials}
              loading={loading}
              filterType={filterType}
              selectedColor={selectedColor}
              setFilterType={filter => {
                setFilterType(filter);
                upholsteryFilteredEvent(
                  filter,
                  selectedColor,
                  selectedSortOption
                );
              }}
              setSelectedColor={color => {
                setSelectedColor(color);
                upholsteryFilteredEvent(filterType, color, selectedSortOption);
              }}
              selectedMaterial={selectedMaterial}
              onFabricInfoClick={newMaterialId => {
                onFabricInfoClick(newMaterialId);
              }}
              filterCounts={filterCounts}
              fabricFavProps={fabricFavProps}
              fabricFavInfoToShow={getFabricFavInfoToShow(upholsteryOptionName)}
            />
          </BrowserOnly>
        </Route>
      </Switch>

      <div
        className={classNames('relative', {
          '[border-bottom:1px_solid_#e5e5e5] mb-2 pb-8': mode !== 'quickview',
        })}
      >
        <div>
          {pdpSelectedCustomizerTab === PDP_DECIDELATER_TAB_ID ? (
            <PDPSelectUpholsteryDecideLater />
          ) : null}

          {mode !== 'quickview' &&
          (isMultiSelectFilters || filterType.includes(PDP_FILTER_TYPES.ALL)) &&
          upholsteryOptionId === FABRIC_PRODUCT_OPTION_ID &&
          !isMobile ? (
            <div className="items-center flex justify-between w-full mb-4 [&_>_*_+_*]:flex-1 [&_>_*_+_*]:ml-4 [&_>_*_+_*]:mt-0 [&_>_*_+_*]:mb-0">
              <PDPUpholsteryColorFilters
                selectedColor={selectedColor}
                setSelectedColor={color => {
                  setSelectedColor(color);
                  upholsteryFilteredEvent(
                    filterType,
                    color,
                    selectedSortOption
                  );
                }}
              />
              <PDPUpholsterySortBy
                selectedOption={selectedSortOption}
                setSelectedOption={sortOption => {
                  setSelectedSortOption(sortOption);
                  upholsteryFilteredEvent(
                    filterType,
                    selectedColor,
                    sortOption
                  );
                }}
              />
            </div>
          ) : (
            <div />
          )}

          {pdpSelectedCustomizerTab === PDP_CUSTOMIZE_TAB_ID ||
          pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID ? (
            <div>
              {!!isPdpMode &&
              pdpSelectedCustomizerTab === PDP_CUSTOMIZE_TAB_ID ? (
                upholsteryOptionName === 'Leather' ? (
                  <div className="text-sm -mt-8 mx-0 mb-8 font-bold">
                    {getFabricFavInfoToShow('Leather')}
                  </div>
                ) : (
                  <div className="mt-0 mx-0 mb-[22px] space-y-2 max-[1023px]:-ml-10 max-[1023px]:-mr-5 [&::-webkit-scrollbar]:max-[1023px]:hidden">
                    <PDPUpholsteryFilters
                      filtersCollectionVisible={filterType}
                      setFiltersCollectionVisible={filter => {
                        setFilterType(filter);
                        upholsteryFilteredEvent(
                          filter,
                          selectedColor,
                          selectedSortOption
                        );
                      }}
                      filterCounts={filterCounts}
                      loading={loading}
                      showFabricFavs={!!fabricFavProps.actions.setOptionAsFav}
                      isClearanceFabricAvailable={isClearanceFabricAvailable}
                      fabricFavInfoToShow={
                        filterType === 'favorites'
                          ? getFabricFavInfoToShow(upholsteryOptionName)
                          : ''
                      }
                      variant="PDPSelectUpholstery"
                    />
                    {isMobile && (
                      <PDPUpholsteryColorFilters
                        selectedColor={selectedColor}
                        showRowInMobile
                        setSelectedColor={color => {
                          setSelectedColor(color);
                          upholsteryFilteredEvent(
                            filterType,
                            color,
                            selectedSortOption
                          );
                        }}
                        showMobileColorFilter
                      />
                    )}
                    {!loading && ungroupedMaterialsArray.length === 0 && (
                      <span className="text-xs font-bold text-gray">
                        Try removing a filter to see more fabric options
                      </span>
                    )}
                  </div>
                )
              ) : (
                <div />
              )}

              <div className="flex flex-col items-start">
                <PDPActiveFabric
                  isEditOrder={isEditOrder}
                  isModularBundleProduct={pdpIsModularBYOProduct}
                  isSelectedOptionsQuickship={
                    pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID &&
                    isSelectedOptionsQuickship
                  }
                  loading={loading}
                  onClick={() => {
                    setGalleryType('FabricMultiImg');
                    setSidebarOpen(true);
                  }}
                  selectedOption={selectedMaterial}
                  selectedOptionsStockQty={selectedOptionsStockQty}
                  size={mode === 'quickview' ? 'xs' : undefined}
                  showInfoIcon={!!selectedFabric}
                />
              </div>

              {(filterType === PDP_FILTER_TYPES.ALL || isMultiSelectFilters) &&
              selectedSortOption === 'family' ? (
                <>
                  {isSortByFlyOutVisible && (
                    <FlyoutContainer
                      top={
                        sortByFlyOutContent.top > 300
                          ? sortByFlyOutContent.top - 300
                          : sortByFlyOutContent.top
                      }
                      title={sortByFlyOutContent.title}
                      content={sortByFlyOutContent.content}
                      subTitle={sortByFlyOutContent.subTitle}
                      imageUrl={sortByFlyOutContent.imageUrl}
                      titleInfo={sortByFlyOutContent.titleInfo}
                      titleExtra={sortByFlyOutContent.titleExtra}
                      subSubTitle={sortByFlyOutContent.subSubTitle}
                      onClose={() => setIsSortByFlyOutVisible(false)}
                      isOpen={isSortByFlyOutVisible}
                    />
                  )}
                  <div className="p-6 max-h-[472px] overflow-y-auto bg-gray-light9 border border-solid border-gray-light7">
                    {Object.keys(materialsGroupedByFamily)
                      .sort()
                      .map(groupName => (
                        <div key={groupName} className="mb-8">
                          <div className="text-sm font-bold text-gray">
                            {groupName}
                          </div>
                          <MaterialThumbnailGroup
                            disabledMaterialIds={disabledMaterialIds}
                            selectedMaterial={selectedMaterial}
                            materials={materialsGroupedByFamily[groupName]}
                            onClick={onOptionValueSelection}
                            onHover={onOptionValueHover}
                            onToggleFlyOut={(content, isVisible) => {
                              setSortByFlyOutContent(content);
                              setIsSortByFlyOutVisible(isVisible);
                            }}
                            isModularBundleProduct={pdpIsModularBYOProduct}
                            fabricFavProps={fabricFavProps}
                            isEditOrder={isEditOrder}
                            flashSaleOptionIds={flashSaleOptionIds}
                            optionId={upholsteryOptionId}
                          />
                        </div>
                      ))}
                  </div>
                </>
              ) : (filterType === PDP_FILTER_TYPES.ALL ||
                  isMultiSelectFilters) &&
                selectedSortOption === 'tier' ? (
                <div>
                  {Object.keys(materialsGroupedByTier).map(tierId => {
                    const tier = tiers[tierId];
                    return tier ? (
                      <div key={tierId} className="mt-8">
                        <div className="text-sm font-bold text-gray">
                          {tier.title}
                        </div>

                        <div className="text-xs text-gray mb-[1.2rem]">
                          {tier.desc}
                        </div>

                        <MaterialThumbnailGroup
                          disabledMaterialIds={disabledMaterialIds}
                          selectedMaterial={selectedMaterial}
                          materials={materialsGroupedByTier[tierId]}
                          onClick={onOptionValueSelection}
                          onHover={onOptionValueHover}
                          isModularBundleProduct={pdpIsModularBYOProduct}
                          fabricFavProps={fabricFavProps}
                          isEditOrder={isEditOrder}
                          flashSaleOptionIds={flashSaleOptionIds}
                          optionId={upholsteryOptionId}
                        />
                      </div>
                    ) : (
                      <div />
                    );
                  })}
                </div>
              ) : (
                <div
                  className={classNames(
                    'max-[1023px]:-ml-5 max-[1023px]:-mr-5 max-[1023px]:md:ml-[-3.333vw] max-[1023px]:md:mr-[-3.333vw]',
                    {
                      'max-[1023px]:md:!m-0': mode === 'quickview',
                    }
                  )}
                >
                  <MaterialThumbnailGroup
                    disabledMaterialIds={disabledMaterialIds}
                    enableFlyout={mode === 'pdp'}
                    fabricFavProps={fabricFavProps}
                    flashSaleOptionIds={flashSaleOptionIds}
                    isEditOrder={isEditOrder}
                    isModularBundleProduct={pdpIsModularBYOProduct}
                    loading={loading}
                    materials={ungroupedMaterialsArray}
                    moreMaterialsCount={moreMaterialsCount}
                    onClick={onOptionValueSelection}
                    onHover={onOptionValueHover}
                    onMoreMaterialsClick={onMoreMaterialsClick}
                    optionId={upholsteryOptionId}
                    selectedMaterial={selectedMaterial}
                    showMoreMaterialsOnDesktop={mode === 'quickview'}
                    variant={
                      mode === 'quickview'
                        ? 'PDPSelectUpholsteryUI--quickview'
                        : 'PDPSelectUpholsteryUI'
                    }
                  />
                </div>
              )}

              {!!isPdpMode &&
              pdpSelectedCustomizerTab !== PDP_READYTOSHIP_TAB_ID ? (
                !isEditOrder && (
                  <>
                    {loading ? (
                      <div className="items-center flex justify-between mt-5 mx-0 mb-0 w-full lg:mt-4 lg:mx-0 lg:mb-0">
                        <Placeholder
                          type="rect"
                          loading
                          style={{
                            height: '43px',
                            width: '152px',
                            borderRadius: '43px',
                          }}
                        />

                        <Placeholder
                          type="rect"
                          loading
                          style={{
                            height: '23px',
                            width: '175px',
                            marginRight: '0',
                          }}
                        />
                      </div>
                    ) : (
                      <div className="items-center flex justify-between mt-5 mx-0 mb-0 w-full lg:mt-4 lg:mx-0 lg:mb-0">
                        <div>
                          <PDPFreeSwatchKitButton
                            styling="rainbow-button"
                            onClick={() => {
                              setIsOrderSwatchesSidebarOpen(true);
                            }}
                          />

                          <PDPOrderSwatchesSidebar
                            show={isOrderSwatchesSidebarOpen}
                            onClose={() => {
                              setIsOrderSwatchesSidebarOpen(false);
                            }}
                            closeSlidePanel={() => {
                              setIsOrderSwatchesSidebarOpen(false);
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {isDesktop && (
                      <div className="mt-4 mx-0 mb-0">
                        <PDPDecideLaterPromo />
                      </div>
                    )}
                  </>
                )
              ) : (
                <>
                  <div />
                  <div />
                </>
              )}
            </div>
          ) : (
            <div />
          )}
        </div>

        <PDPFabricDetailSidebar
          isOpen={!!selectedMaterial?.materialId && isSidebarOpen}
          onClose={() => {
            setGalleryType('');
            setSidebarOpen(false);
          }}
          fabricId={
            isFullScreenOpen || isCompareToolOpen
              ? sideBarSelectedFabricId
              : selectedMaterial?.materialId
          }
          mode={mode}
          galleryType={galleryType}
          mobileHideHeader
        />
      </div>
    </>
  );
};

PDPSelectUpholsteryUI.propTypes = {
  allMaterialsLength: PropTypes.number,
  disabledMaterialIds: PropTypes.arrayOf(PropTypes.number),
  fabricFavProps: PropTypes.object,
  flashSaleOptionIds: PropTypes.arrayOf(PropTypes.number),
  filterCounts: productFilterTypes,
  filterType: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  isEditOrder: PropTypes.bool,
  isSelectedOptionsQuickship: PropTypes.bool,
  loading: PropTypes.bool,
  materials: PropTypes.arrayOf(materialPropType),
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  onMoreMaterialsClick: PropTypes.func,
  onOptionValueHover: PropTypes.func,
  onOptionValueSelection: PropTypes.func,
  selectedColor: PropTypes.string,
  selectedMaterial: materialPropType,
  selectedOptionsStockQty: PropTypes.number,
  setFilterType: PropTypes.func,
  setSelectedColor: PropTypes.func,
  upholsteryFilteredEvent: PropTypes.func,
  upholsteryOptionId: PropTypes.number,
  upholsteryOptionName: PropTypes.string,
  isClearanceFabricAvailable: PropTypes.bool,
};

export default PDPSelectUpholsteryUI;
